import { cloneDeep, merge } from 'lodash'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { defaultSettings, GSSettings } from 'components/containers/settings/utils/defaultSettings'
import { GetSettingsApiResponse } from 'gen/rtk-openapi'

interface YardDetails {
  addressName: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  logo: string
  country: string
  phoneNumber?: string
  locationId?: string
  defaultLocationId?: string
  gsPayEnabled?: boolean
}
interface SettingsState {
  settings: GSSettings
  details: YardDetails
  cashDrawers: any[]
  selectedCashDrawerId?: string
  defaultCurrency: string
  defaultYardUm: string
  hasAtm: boolean
  /**
   * Flag to indicate if settings have been loaded from the backend
   */
  yardSettingsLoaded: boolean
}

const initialState: SettingsState = {
  settings: defaultSettings,
  details: {
    addressName: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    logo: '',
    phoneNumber: '',
    locationId: '',
    gsPayEnabled: false,
  },
  cashDrawers: [],
  selectedCashDrawerId: undefined,
  defaultCurrency: 'USD',
  defaultYardUm: 'lbs',
  hasAtm: false,
  yardSettingsLoaded: false,
}

// This function handles merge logic for certain default settings (eg. array-type settings)
// Keep them if the yard does not have those settings in the DB.
// Otherwise, remove them before the merge

// NOTE: Make sure to use optional chaining in case setting does not exist on BE
export const mergeSettings = (
  settings: typeof defaultSettings,
  newSettings: Partial<GSSettings> | GetSettingsApiResponse,
) => {
  const newDefaultSettings: any = cloneDeep(settings)
  if (newSettings?.purchaseSettings?.deductions?.listOfDeductions) {
    delete newDefaultSettings.purchaseSettings?.deductions?.listOfDeductions
  }
  if (newSettings?.inventorySettings?.finishedGoods?.listOfFinishedGoods) {
    delete newDefaultSettings.inventorySettings.finishedGoods.listOfFinishedGoods
  }
  if (newSettings?.documentSettings?.general?.documentsWithTimestamps) {
    delete newDefaultSettings.documentSettings.general.documentsWithTimestamps
  }
  if (newSettings?.adminSettings?.rbac) {
    delete newDefaultSettings.adminSettings?.rbac
  }
  if (newSettings?.purchaseSettings?.inboundTickets?.fieldVisibility) {
    delete newDefaultSettings.purchaseSettings?.inboundTickets.fieldVisibility
  }
  return merge({}, newDefaultSettings, newSettings)
}

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setSettings: (state, action: PayloadAction<Partial<typeof defaultSettings>>) => {
      state.settings = mergeSettings(state.settings, action.payload || {})
    },
    setYardDetails: (state, action: PayloadAction<YardDetails>) => {
      state.details = action.payload
    },
    setCashDrawers: (state, action: PayloadAction<any[]>) => {
      state.cashDrawers = action.payload
    },
    selectCashDrawer: (state, action: PayloadAction<string>) => {
      state.selectedCashDrawerId = action.payload
    },
    setDefaultCurrency: (state, action: PayloadAction<string>) => {
      state.defaultCurrency = action.payload
    },
    setDefaultYardUm: (state, action: PayloadAction<string>) => {
      state.defaultYardUm = action.payload
    },
    setHasAtm: (state, action: PayloadAction<boolean>) => {
      state.hasAtm = action.payload
    },
    setYardSettingsLoaded: (state, action: PayloadAction<boolean>) => {
      state.yardSettingsLoaded = action.payload
    },
  },
})

export const {
  setSettings,
  setYardDetails,
  setCashDrawers,
  selectCashDrawer,
  setDefaultCurrency,
  setDefaultYardUm,
  setHasAtm,
  setYardSettingsLoaded,
} = settingsSlice.actions

export default settingsSlice.reducer
